/**
 * DobField — the lead's date of birth, inline in LeadHeader's identity row
 * beside phone / email (owner ask 2026-09-22: "a date of birth section near a
 * lead's name").
 *
 *   unset   → a quiet "Add DOB" link
 *   set     → "DOB 03/10/1990 (36)" — click to change
 *   editing → native date input + save / cancel (Enter saves, Escape cancels;
 *             an emptied input saves as "cleared")
 *
 * PATCHes /api/crm/contacts/:id { date_of_birth } itself, like OwnerSelect,
 * then hands the server's canonical value (and the contact id it was saved
 * for) to `onChanged`. The displayed value
 * is always the `value` prop — the parent's copy — so an edit made from
 * EditContactModal shows here too. The lead page is NOT remounted on :id
 * changes, so the caller keys this by contact id to drop a half-typed draft.
 */
import { useState } from 'react';
import { Cake, Check, Loader2, X } from 'lucide-react';
import api from '../../../services/api';
import { ageFromDob, dayKey, formatDob } from '../../lib';

interface DobFieldProps {
  contactId: string;
  value: string | null | undefined;
  /** Receives the id it saved for, so a save that lands after the page has
   *  moved to another lead can be dropped by the parent. */
  onChanged: (contactId: string, next: string | null) => void;
}

// fieldCls, sized down to sit on the header's text-xs/sm identity line.
const dateInputCls =
  'rounded-lg border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:text-sm';

const smallIconBtn =
  'inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50';

export default function DobField({ contactId, value, onChanged }: DobFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shown = formatDob(value);
  const age = ageFromDob(value);

  const startEdit = () => {
    setDraft(value || '');
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    if (saving) return;
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    if (saving) return;
    const next = draft.trim() || null;
    if (next === (value || null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/api/crm/contacts/${contactId}`, { date_of_birth: next });
      onChanged(contactId, res.data?.contact?.date_of_birth ?? next);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save the date of birth.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <Cake className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); }
          }}
          min="1900-01-01"
          max={dayKey(new Date().toISOString())}
          disabled={saving}
          autoFocus
          aria-label="Date of birth"
          className={dateInputCls}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          aria-label="Save date of birth"
          title="Save"
          className={`${smallIconBtn} text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          aria-label="Cancel editing date of birth"
          title="Cancel"
          className={`${smallIconBtn} text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
        {error && (
          <span role="alert" className="text-xs text-red-600 dark:text-red-400">
            {error}
          </span>
        )}
      </span>
    );
  }

  if (!shown) {
    return (
      <button
        type="button"
        onClick={startEdit}
        title="Record this lead's date of birth"
        className="inline-flex items-center gap-1.5 rounded text-primary-700 transition-colors duration-150 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
      >
        <Cake className="h-3.5 w-3.5 flex-shrink-0" /> Add DOB
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="Date of birth — click to change"
      aria-label={`Date of birth ${shown}${age !== null ? `, age ${age}` : ''}. Change date of birth`}
      className="inline-flex items-center gap-1.5 rounded transition-colors duration-150 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200"
    >
      <Cake className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
      <span className="tabular-nums">
        DOB {shown}
        {age !== null && <span className="text-gray-400 dark:text-gray-500"> ({age})</span>}
      </span>
    </button>
  );
}
